<template>
<div class="clone-metric clone-metric--revenue" :title="title">
  <div class="clone-metric__label">
    <span class="clone-metric__dot" :style="{ backgroundColor: color }"></span>
    <span>{{ label }}</span>
    <!-- The unit the figures inside are in, beside the name rather than inside each of them: the
         same place every other section on this page names its own. -->
    <span v-if="unit" class="clone-metric__unit">{{ unit }}</span>
    <span v-if="factor" class="clone-metric__factor">{{ factor }}</span>
  </div>
  <!-- THE HEADLINE: the figure, typed. It is named only where the card asks a second question under
       it — with the columns and their buttons below, the uppercase label names the section and
       something has to say which of the two the number is — and the name is a real <label for>, so
       clicking the word puts the caret in the field. A card that asks nothing else leaves it
       unnamed and the label above is the whole of it.

       The field is as wide as the widest figure it can hold and no wider — see `maxLength`, which is
       what a full-width box would otherwise stretch a ten-digit field across half a page for. What
       is left of the row carries the readings OF that figure, where a card passes them: an average
       per month is the typed total divided by the window, and it belongs beside the number it is a
       division of rather than at the far bottom of the card, a chart away from it. -->
  <div class="clone-metric__headline">
    <div class="clone-metric__headline-field">
      <label v-if="valueLabel" class="clone-metric__ask" :for="fieldId" :title="valueTitle">{{ valueLabel }}</label>
      <v-text-field
        :id="fieldId"
        ref="valueField"
        :key="inputKey"
        :value="value"
        class="clone-metric__input"
        :class="{ 'clone-metric__input--short': maxLength > 0 }"
        :maxlength="maxLength > 0 ? maxLength : null"
        hide-details
        single-line
        @change="$emit('input', $event)"
      ></v-text-field>
      <!-- What the figure above is held above, said before it is typed rather than after. A wizard
           that knows the wall and waits for the user to walk into it is one that had the answer all
           along; a card passing nothing here shows nothing, which is every card that has no wall to
           name. hide-details on the field is deliberate — this is the card's own line, in the card's
           own small grey, not Vuetify's message slot under a box that is not the only thing in the
           row. -->
      <p v-if="valueHint" class="clone-metric__hint">{{ valueHint }}</p>
    </div>
    <!-- Built exactly like the field beside it — the small grey name over the figure — because it is
         the same kind of thing: a figure about this year, in this year's money. It is set in the
         headline's size and reads in the headline's type. A reading of the total in footnote type
         next to the total in display type would have said it was a lesser fact; it is the same fact,
         per month.

         AND WHERE THE PARENT SAYS SO, IT IS THE SAME FACT TYPED. A reading that is one arithmetic
         step from the headline can be stated in place of it — a year is as answerable by the month
         as by the year, and which of the two the user knows is theirs to say, not this card's to
         assume. It is then a field in the field's own dress, black and underlined, because the one
         thing that separated the two was that this one could not be typed into. The card does no
         arithmetic either way: what was typed goes up as text to the parent that formatted it. -->
    <div v-for="(entry, index) in aside" :key="`aside_${entry.label}`" class="clone-metric__aside" :title="entry.title">
      <component
        :is="entry.editable ? 'label' : 'span'"
        class="clone-metric__ask"
        :class="{ 'clone-metric__ask--static': !entry.editable }"
        :for="entry.editable ? asideId(index) : null">{{ entry.label }}</component>
      <v-text-field
        v-if="entry.editable"
        :id="asideId(index)"
        :key="entry.inputKey || `aside_field_${index}`"
        :value="entry.value"
        class="clone-metric__input"
        :class="{ 'clone-metric__input--short': maxLength > 0 }"
        :maxlength="maxLength > 0 ? maxLength : null"
        hide-details
        single-line
        @change="$emit('aside-input', { index: index, value: $event })"
      ></v-text-field>
      <span v-else class="clone-metric__aside-value">{{ entry.value }}</span>
      <!-- The entry's own line, for the reason the headline has one: what the figure comes to, or
           what it was held at, said beside it in the card's small grey. -->
      <p v-if="entry.hint" class="clone-metric__hint">{{ entry.hint }}</p>
    </div>
  </div>

  <!-- WHAT MAY BE DONE TO THE YEAR DRAWN BELOW, over the right-hand end of the row it acts on. They
       were named patterns — Flat, Random — which is a choice between two years and not a thing you
       do to the one on screen: pressing Flat threw away whatever had been typed, and Random rolled a
       whole new year for a reader who only wanted this one less even. These are verbs instead, and
       each press moves the row it is over a little further in its own direction, so how uneven the
       year is is arrived at rather than picked.

       Icons and not words, at the end of the row: the year underneath is what the reader is looking
       at, and two buttons the width of their own labels standing over it would be the loudest thing
       in the card. Each says what it does on hover. -->
  <div v-if="actions" class="clone-actions">
    <v-btn
      v-for="entry in actions"
      :key="`action_${entry.key}`"
      icon
      x-small
      class="clone-action"
      :title="entry.title"
      :aria-label="entry.title"
      @click="$emit('action', entry.key)">
      <v-icon small>{{ entry.icon }}</v-icon>
    </v-btn>
  </div>

  <!-- The months, split into what produced them: what the model would have made on its own at the
       bottom, what the season adds where a card says so, and what its media spend returns stacked on
       top in the colour that return is drawn in beside it. The stack is the month however the split
       falls — and where a component took revenue OUT of a month, the row grows a line and that part
       hangs under it, the stack above being what the month would have sold without it. Either way
       the twelve add up to the headline above them.

       AND, WHERE THE CARD IS ASKED HOW THE YEAR FALLS, IT IS WHERE THE YEAR IS SAID. Every column is
       a button then: pressing one opens that month's own figure as a field, which is the same
       statement as pressing the custom shape above and says which month as well. A card that only
       draws the row passes nothing and its columns stay the spans they were — the clone's do, its
       year being its source model's history rather than anybody's claim.

       EVERY MONTH SAYS WHAT IT SOLD, over its own column, wherever the row arrives carrying figures.
       The columns are normalised against the tallest of them, so what can be READ off one on its own
       is nothing at all — the height says the shape and the number says the amount, and without the
       number the box is a picture of a year with no size to it. It was in the hover title, which is
       one month at a time and only for a reader who already suspected there was something to ask.

       The segments are sized by flex-basis rather than by height precisely so that the figure above
       them is laid out by the same box: a month at nothing still has its number standing over the
       line, where a percentage of the box would have put it under. The room they leave it is made by
       the row before it gets here — see the ceiling the columns are held under — so nothing about the
       picture is given up for it.

       The field is not IN the column, because a column is a twelfth of the box and a figure in
       dollars is wider than that. It is one field over the plot, anchored on the column that opened
       it and standing exactly where that column's figure was, which is what makes the click that
       swaps one for the other read as one object rather than two. -->
  <div v-if="bars" class="clone-metric__bars" :style="barGap">
    <!-- THE LINE THE MONTHS STAND ON, where any of them has something hanging below it. A column is
         a month's revenue split into what produced it, and a component can produce a NEGATIVE amount
         — a month that sells under its own trend has a season that took revenue out of it, and there
         is no segment inside a bar that can be drawn for something the bar does not contain. So the
         row grows a floor: what a month was reduced by hangs beneath it, the stack above it is what
         the month WOULD have sold, and the difference between the two is the figure written over the
         column. Drawn only when there is something below it — a rule under a row that never goes
         under it is an axis for nothing. It runs under the total beside them too: that column stands
         on the same zero, at its own scale. -->
    <span v-if="zeroLine > 0" class="clone-metric__bar-zero" :style="{ bottom: zeroLine + '%' }"></span>
    <component
      :is="monthsEditable && !entry.apart ? 'button' : 'span'"
      v-for="(entry, index) in columns"
      :key="`total_bar_${entry.key}`"
      :type="monthsEditable && !entry.apart ? 'button' : null"
      class="clone-metric__bar"
      :class="{
        'clone-metric__bar--live': monthsEditable && !entry.apart,
        'clone-metric__bar--editing': editing === index,
        'clone-metric__bar--apart': entry.apart
      }"
      :style="entry.apart ? apartStyle : null"
      :title="entry.parts ? null : entry.totalTitle"
      :aria-label="entry.totalTitle"
      @mouseenter="hovered = index"
      @mouseleave="onLeave(index)"
      @focus="hovered = index"
      @blur="onLeave(index)"
      @click="onBar(index)">
      <!-- Everything the column is credited WITH, stacked on the line and read upwards: what the
           model makes on its own, what the season and the spot events add to it where they add
           anything, and what the media return on top. The percentages are of this region and not of
           the column, so a card that draws no floor passes zeroLine 0, this box is the whole column,
           and every figure means exactly what it always meant.

           The markup runs the other way round from the stack — this box is laid out from the top
           down — so the order below is media, spot, season, base and the column reads base, season,
           spot, media from the line up. That is the waterfall's order, said twelve times. -->
      <span class="clone-metric__bar-up" :style="{ flexBasis: (100 - zeroLine) + '%' }">
        <span
          v-if="showAmounts"
          class="clone-metric__bar-value"
          :class="{ 'clone-metric__bar-value--hidden': editing === index }">{{ entry.amount }}</span>
        <span class="clone-metric__bar-fill clone-metric__bar-fill--media" :style="{ flexBasis: entry.media + '%' }"></span>
        <span v-if="entry.spot" class="clone-metric__bar-fill clone-metric__bar-fill--spot" :style="{ flexBasis: entry.spot + '%' }"></span>
        <span v-if="entry.season" class="clone-metric__bar-fill clone-metric__bar-fill--season" :style="{ flexBasis: entry.season + '%' }"></span>
        <span class="clone-metric__bar-fill clone-metric__bar-fill--base" :style="{ flexBasis: entry.base + '%', backgroundColor: color }"></span>
      </span>
      <!-- And what it was reduced BY, hanging under the line and read downwards. Up to two segments,
           each in its own component's colour: they are the same amber and the same blue as above the
           line, seen from the other side of their own zero. Season first, so that the distance from
           the line to a component is the same on both sides of it. -->
      <span v-if="zeroLine > 0" class="clone-metric__bar-down" :style="{ flexBasis: zeroLine + '%' }">
        <span v-if="entry.below" class="clone-metric__bar-fill clone-metric__bar-fill--season" :style="{ flexBasis: entry.below + '%' }"></span>
        <span v-if="entry.belowSpot" class="clone-metric__bar-fill clone-metric__bar-fill--spot" :style="{ flexBasis: entry.belowSpot + '%' }"></span>
      </span>
    </component>

    <!-- WHAT THE COLUMN IS MADE OF, NAMED AND IN ITS OWN COLOURS, where the row arrived carrying the
         split in parts. A native title is one grey line of arithmetic and this is a stack of
         components with signs on them: each row carries the same dot the segment it names is drawn
         in, so a row of the card and a band of the column are read as one object.

         Written here rather than with v-tooltip because Vuetify's tooltip lifts its activator out of
         the DOM and re-inserts it beside itself, and this row is thirteen activators rebuilt on every
         keystroke. It is the same mechanism the month's own field already uses: one element over the
         plot, anchored on the column it belongs to. Above the chart rather than over it — a card
         explaining a column must not stand on the column — and deaf to the pointer so moving onto it
         never counts as leaving the column that opened it.

         A column that carries no `parts` never draws this and keeps the plain title it always had. -->
    <div v-if="tip" class="clone-bar-tip" :style="tipStyle">
      <div class="clone-bar-tip__head">{{ tip.month }}</div>
      <div v-for="part in tip.parts" :key="`${tip.key}_${part.name}`" class="clone-bar-tip__row">
        <span class="clone-bar-tip__dot" :style="{ backgroundColor: part.color }"></span>
        <span class="clone-bar-tip__name">{{ part.name }}</span>
        <span class="clone-bar-tip__value">{{ part.value }}</span>
      </div>
      <div class="clone-bar-tip__row clone-bar-tip__row--sum">
        <span class="clone-bar-tip__dot clone-bar-tip__dot--none"></span>
        <span class="clone-bar-tip__name">{{ tip.totalLabel }}</span>
        <span class="clone-bar-tip__value">{{ tip.amount }}</span>
      </div>
    </div>
    <input
      v-if="editing !== null"
      ref="monthField"
      class="clone-metric__month"
      :style="monthStyle"
      :value="editingAmount"
      :aria-label="editingLabel"
      @click.stop
      @keydown.esc.exact="cancelMonth"
      @keydown.enter.exact.prevent="commitMonth"
      @keydown.tab.exact.prevent="stepMonth($event, 1)"
      @keydown.shift.tab.prevent="stepMonth($event, -1)"
      @blur="commitMonth">
  </div>
  <!-- The x axis of those columns: one letter per month, in a row of its own with the same gap and
       the same flex, so a letter stands under the bar it names without taking any of the height the
       bar's own percentages are read against. The total names itself in full — it is not one of
       twelve of anything, so there is no initial to take. -->
  <div
    v-if="bars"
    class="clone-metric__bar-axis"
    :class="{ 'clone-metric__bar-axis--dense': dense }"
    :style="barGap">
    <span
      v-for="entry in columns"
      :key="`total_axis_${entry.key}`"
      class="clone-metric__bar-tick"
      :class="{ 'clone-metric__bar-tick--apart': entry.apart }"
      :style="entry.apart ? apartStyle : null"
      :title="entry.totalTitle">{{ entry.apart ? entry.initial : entry.initial.slice(0, 1) }}</span>
  </div>

  <!-- WHATEVER ELSE IS SAID ABOUT THIS YEAR, under the year itself and inside the same card. The
       composer puts its seasonality and its spot events here: what share of each month's revenue is
       seasonal, and what share of it happened once, are two further statements about the twelve
       columns above, made after they have been shaped, and the first was a card of its own a page
       below the row it is about. A slot rather than a prop bundle because the card
       owns no arithmetic and this owns none of the slot's either — it is the caller's block, given
       the caller's width and the columns' own alignment. A card that passes nothing is unchanged. -->
  <slot name="beneath"></slot>

  <ekrm-level-rail
    :range="range"
    :slider="slider"
    :axis="axis"
    :ticks-height="ticksHeight"
    :color="color"
    :track-color="trackColor"
    id-prefix="total"
    @slide="$emit('slide', $event)"
    @commit="$emit('commit', $event)"
    @jump="$emit('jump', $event)" />

  <!-- A card whose readings stand beside the figure instead passes no foot and gets no rule across
       the bottom of it — an empty strip under a chart reads as a row that failed to fill. -->
  <div v-if="foot && foot.length > 0" class="clone-metric__foot">
    <span v-for="entry in foot" :key="entry.label" class="clone-foot-pair" :title="entry.title">
      <span class="clone-metric__foot-label">{{ entry.label }}</span>
      <span class="clone-metric__foot-value">{{ entry.value }}</span>
    </span>
  </div>
</div>
</template>

<script>
// The Total Revenue box, shared by both EKRM wizards: the figure, typed; optionally the buttons
// that move the year it is spread across; the months under them split where the curves split them;
// an optional corridor; and a footer.
//
// It owns no arithmetic at all — every figure arrives formatted, every bar arrives as a percentage
// and every bound arrives as a style. See EkrmChannelCard for why.
import EkrmLevelRail from './EkrmLevelRail.vue'

// How wide a month's field is. A figure in dollars is what it has to hold — 480.00k, and a digit
// more for a business that sells in millions — and the columns are a twelfth of the card apiece, so
// this is a width in pixels rather than a share of anything.
const MONTH_FIELD = 92
// And how tall, which is the room it has to be given at the top of the box when the month it belongs
// to is the tallest one there.
const MONTH_HEIGHT = 24

// How wide the card that names a month's components is. Fixed, because it stands over a column and
// is centred on it: a box whose width followed its longest figure would move sideways as the pointer
// walked the row.
const TIP_WIDTH = 186

// The column the total stands in, and the air between it and December: the same two numbers the
// seasonality editor's own total is built from, which is what puts the two rules on one line and
// ends both plots at the same place. The air is taken out of the column's margin rather than added
// to it, the row already carrying a gap between every pair of columns — see apartStyle.
const APART_WIDTH = 96
const APART_GAP = 12

export default {
  name: 'EkrmRevenueCard',
  components: { EkrmLevelRail },
  props: {
    label: { type: String, required: true },
    // What the card's figures are counted in, named once on the card's own label. Empty leaves it
    // unnamed, which is what a card whose unit is obvious from its boxes needs.
    unit: { type: String, default: '' },
    title: { type: String, default: '' },
    // Already formatted. The key is bumped by the parent on every commit so Vuetify's internal copy
    // of a rejected or re-rounded value is discarded rather than shown back.
    value: { type: String, default: '' },
    inputKey: { type: String, default: 'revenue' },
    // What the figure is, and what it means, where the card asks more than one thing — see the
    // template. Empty leaves the field unnamed under the card's own label.
    valueLabel: { type: String, default: '' },
    // One line under the headline field, for a card that has something to say about what may be
    // typed into it. Empty for every card that has not.
    valueHint: { type: String, default: '' },
    valueTitle: { type: String, default: '' },
    // WHAT THE CARD OFFERS TO DO TO THE ROW: [{ key, icon, title }], drawn as icon buttons over the
    // right-hand end of it and handed straight back as `action`. The card owns no arithmetic and no
    // words here either — it does not know what flattening a year is, only that a button was
    // pressed. A card that is only asked for the figure passes nothing and draws no row of buttons.
    actions: { type: Array, default: null },
    // The 'x1.25' badge, or empty where the figure is not a multiplier over anything.
    factor: { type: String, default: '' },
    color: { type: String, default: '#43A047' },
    trackColor: { type: String, default: 'rgba(67, 160, 71, 0.18)' },
    // [{ key, initial, totalTitle, base, media }] or null, plus `amount` — the month's own figure,
    // formatted — where the columns can be typed into, and `season`/`spot` with `below`/`belowSpot`
    // where a component of the month can stand above or hang below the line. `base`, `media`,
    // `season` and `spot` are percentages of the region ABOVE the line, `below` and `belowSpot` of
    // the region under it. The two signed components each own a segment on each side of the line and
    // are drawn on whichever side they landed on; a card that passes neither draws the two-segment
    // column it always drew.
    bars: { type: Array, default: null },
    // How much of each column is the region under the line, as a percentage of its height. 0 — the
    // default, and what every card that draws no negative component passes — is a row with no floor
    // at all: the region above is the whole column, and every percentage in `bars` is a percentage of
    // the column exactly as it always was.
    zeroLine: { type: Number, default: 0 },
    // THE THIRTEENTH COLUMN, or null for a row that is only twelve. It arrives shaped exactly like
    // one of `bars` — the same segments, the same figure over it, the same parts for its card — and
    // is drawn by the same markup, because it is the same picture of the same three components: the
    // year, rather than a month of it. Its `initial` is a word and not a letter, it being the one
    // column that is not one of twelve of anything.
    //
    // AT ITS OWN SCALE, which is what the rule down its left says. A year is twelve months tall and
    // would leave the months as a row of hairlines beside it; the caller sizes its segments against
    // the year and this card sets it apart, and neither of them pretends the two share an axis.
    total: { type: Object, default: null },
    barGap: { type: Object, default: null },
    dense: { type: Boolean, default: false },
    // Whether a column is a statement the user can make. Off is the card as it always was: a reading
    // of a row that came from somewhere else.
    monthsEditable: { type: Boolean, default: false },
    // { marks, blocked } or null for a figure nothing bounds — see the template.
    range: { type: Object, default: null },
    slider: { type: Object, default: null },
    axis: { type: Object, default: null },
    ticksHeight: { type: String, default: '0px' },
    // [{ label, value, title }]
    foot: { type: Array, default: function () { return [] } },
    // The same shape, read beside the figure rather than under the whole card — see the template.
    // An entry with `editable` is typed instead of read, and carries an `inputKey` the parent bumps
    // the way it bumps the headline's; what is typed comes back on `aside-input` as { index, value }.
    aside: { type: Array, default: function () { return [] } },
    // The longest figure the field accepts, in characters as they are TYPED, separators included:
    // ten digits is '1,000,000,000', which is thirteen. 0 leaves the field unbounded.
    maxLength: { type: Number, default: 0 }
  },
  data () {
    return {
      // The month whose figure is a field rather than a column, or null. One at a time: a month
      // typed moves the total the card's headline states, so two fields open at once would be
      // showing one figure the user typed and one the typing has already moved past.
      editing: null,
      // The column under the pointer or the keyboard, or null. It names the card standing above the
      // row and nothing else — no figure moves for it.
      hovered: null,
      // Set for exactly one flush while Tab is carrying the field from one month to the next: the
      // commit on the way out moves the row, and the watcher that closes a field when the row moves
      // is the one thing that must not close this one. See stepMonth.
      stepping: false
    }
  },
  computed: {
    // Unique per card rather than per page: two of these are never mounted at once today, and an id
    // is what ties the label to the field for a screen reader, so it takes the key the parent
    // already bumps — the label follows the field it names through every re-key.
    fieldId () {
      return `ekrm_revenue_${this.inputKey}`
    },
    // The row as it is drawn: the twelve, and the year beside them where there is one. Everything
    // downstream indexes into this — the card above a column, the field inside one, the axis under
    // them — so there is one list and one set of indices rather than a row with an exception in it.
    columns () {
      if (!this.bars) return []
      return this.total ? this.bars.concat([this.total]) : this.bars
    },
    // The width the total's column takes out of the row, and the air before it. The gap is taken out
    // of the margin because the row already puts one between every pair of columns: what is wanted is
    // twelve pixels from December to the rule, however wide the row's own gap happens to be.
    apartStyle () {
      return {
        flex: `0 0 ${APART_WIDTH}px`,
        marginLeft: `calc(${APART_GAP}px - ${(this.barGap && this.barGap.gap) || '0px'})`
      }
    },
    // How much of the row the total's column and its air take, in pixels. What is left is the twelve
    // months, which is what the card over a column and the field inside one are placed against.
    apartRoom () {
      return this.total ? APART_WIDTH + APART_GAP : 0
    },
    editingBar () {
      if (this.editing === null || !this.bars) return null
      return this.bars[this.editing] || null
    },
    // The month whose components are being named, or null. Only where the row arrived carrying them:
    // a card with nothing in it is a box that appeared for no reason, and a caller that passes no
    // parts keeps the plain title it always had.
    tip () {
      if (this.hovered === null) return null
      const bar = this.columns[this.hovered]
      return bar && bar.parts ? bar : null
    },
    // ABOVE THE CHART AND CENTRED ON ITS OWN COLUMN. Above, because a card explaining a column may
    // not stand on top of it; centred and then held between the row's two edges, the same clamp the
    // month field is under, because a January whose card hung off the left would belong to no
    // picture at all.
    tipStyle () {
      if (this.hovered === null) return null
      return {
        width: `${TIP_WIDTH}px`,
        left: `clamp(0px, calc(${this.centreOf(this.hovered)} - ${TIP_WIDTH / 2}px), calc(100% - ${TIP_WIDTH}px))`
      }
    },
    // What the field opens on: the month's own figure as the card was already drawing it. An edit
    // round-trips through exactly what is on screen, which is also what lets the commit tell a field
    // that was closed again from one that was changed — see commitMonth.
    editingAmount () {
      return this.editingBar ? this.editingBar.amount : ''
    },
    editingLabel () {
      return this.editingBar ? this.editingBar.totalTitle : ''
    },
    // Whether the row arrived with a figure per month. It is the row that decides, not the card: a
    // year is twelve columns and has room for twelve numbers, and a window three years wide has a
    // column narrower than one digit — which is the same question `dense` already answers, so a
    // dense row is drawn without them however it arrived.
    showAmounts () {
      return !!this.bars && !this.dense && this.columns.every(entry => !!entry.amount)
    },
    // OVER THE COLUMN IT BELONGS TO, WHERE THAT COLUMN'S FIGURE WAS, AND INSIDE THE BOX.
    //
    // Two clamps and nothing else. Sideways, the field is wider than a twelfth of the card, so it is
    // centred on its own column and then held between the two edges — a January whose field hung off
    // the left of the card would be a figure belonging to no picture at all. Upwards, it stands on
    // top of its own stack, which is where the caption it replaces was, and stops short of the roof
    // so that the tallest month in a shallow box still has its field on the chart.
    monthStyle () {
      const bar = this.editingBar
      if (this.editing === null || !bar) return null
      const centre = this.centreOf(this.editing)
      // The top of this month's stack, as a percentage of the COLUMN: its segments are percentages
      // of the region above the line, so they are scaled by that region's own share and stood on the
      // line. With no line the region is the column and this is the sum it always was.
      const stack = (Number(bar.base) || 0) + (Number(bar.media) || 0) +
        (Number(bar.season) || 0) + (Number(bar.spot) || 0)
      const top = this.zeroLine + stack * (100 - this.zeroLine) / 100
      return {
        width: `${MONTH_FIELD}px`,
        left: `clamp(0px, calc(${centre} - ${MONTH_FIELD / 2}px), calc(100% - ${MONTH_FIELD}px))`,
        bottom: `min(${top}%, calc(100% - ${MONTH_HEIGHT}px))`
      }
    }
  },
  methods: {
    // The caret put in the headline with what is already there selected, for the step that opens on
    // this card. Same reason the spend card offers one: the figure standing there was written by the
    // wizard and not by the reader, so it is offered ready to be typed over. Called from the page —
    // the card does not know when its step is reached.
    focusValue () {
      const field = this.$refs.valueField
      if (!field) return
      field.focus()
      const el = field.$refs.input
      if (el) el.select()
    },
    // The middle of one column, as a CSS length rather than a percentage. The months share whatever
    // the total's own column leaves — a fixed number of pixels off the row, not a share of it — so a
    // percentage of the row is the wrong unit for anything that has to stand over a month. The total
    // is the last column and is measured back from the right edge for the same reason.
    centreOf (index) {
      const count = this.bars ? this.bars.length : 0
      if (index >= count) return `calc(100% - ${APART_WIDTH / 2}px)`
      const share = count > 0 ? (index + 0.5) / count : 0
      return `calc((100% - ${this.apartRoom}px) * ${share})`
    },
    // The id that ties an aside's name to its field, on the same key the headline's id is built from
    // so that a re-keyed field keeps the label that names it.
    asideId (index) {
      return `${this.fieldId}_aside_${index}`
    },
    // A column let go of by the pointer or the keyboard. Guarded on the month, so the mouseleave of
    // the column being left cannot clear a card the pointer has already opened on the next one.
    onLeave (index) {
      if (this.hovered === index) this.hovered = null
    },
    // A column pressed. It opens that month's figure as a field and says nothing else: the year is
    // now the user's to state, which is what `custom` says, and no figure has moved yet. Pressing
    // the column already open closes it, a press being how it was opened.
    onBar (index) {
      // The total is a reading and not a statement: it is the twelve added up, and the way to change
      // it is to change one of them.
      if (!this.monthsEditable || !this.bars || index >= this.bars.length) return
      if (this.editing === index) {
        this.editing = null
        return
      }
      this.editing = index
      this.$emit('custom')
      this.$nextTick(() => {
        const field = this.$refs.monthField
        const el = Array.isArray(field) ? field[0] : field
        if (el) {
          el.focus()
          el.select()
        }
      })
    },
    // Escape puts the figure back before it closes the field, and that order is the whole of it:
    // taking a focused input out of the DOM can raise a blur on the way out, and a blur is a commit.
    // Restoring the value first leaves that commit with nothing to say — see the guard below.
    cancelMonth (event) {
      if (this.editingBar) event.target.value = this.editingBar.amount
      this.editing = null
    },
    // What was typed, handed up as it was typed: this card owns no arithmetic, so the string goes to
    // the parent that formatted the figure in the first place and knows how to read one back.
    //
    // Enter and blur are one gesture here: a field that closes on Enter has to close on the click
    // that landed somewhere else, or a reader who typed and looked away is left with a caret over a
    // chart. A field opened and closed again says nothing — compared as TEXT, against exactly what
    // the field was showing, which is the one comparison a card with no arithmetic can make.
    commitMonth (event) {
      const index = this.editing
      const shown = this.editingAmount
      this.editing = null
      this.sendMonth(index, shown, event.target.value)
    },
    // What a field says on its way out, however it left: on Enter, on a blur, or on the Tab that
    // opened the next month. `shown` is what the field was opened on and is passed in rather than
    // read, the caller having already moved on to another month by the time some of them call this.
    sendMonth (index, shown, raw) {
      if (index === null) return
      const typed = String(raw === null || raw === undefined ? '' : raw).trim()
      if (typed === '' || typed === String(shown).trim()) return
      this.$emit('month', { index: index, value: typed })
    },
    // TAB WALKS THE YEAR, and December is followed by January. A year stated month by month is
    // twelve figures typed in a row, and the gesture for the next box along is the one every other
    // field on this page already answers to — the alternative being a click on each column, with the
    // hand off the keyboard between every two numbers. Shift is the same walk backwards, so a figure
    // tabbed past is one keystroke away rather than a lap of the row.
    //
    // The commit is the ordinary one: what was typed goes up exactly as Enter would have sent it,
    // and a field tabbed through without being touched says nothing at all.
    //
    // WHAT IS DIFFERENT IS THAT THE FIELD DOES NOT CLOSE. The month above may have just moved the
    // row, and `bars` closes a field whenever the row it was opened on is replaced — which is right
    // for a shape pressed or the headline retyped, and is exactly wrong here. `stepping` holds it
    // off for the one flush that redraw takes, and is cleared on the tick after it so a row that
    // never moved cannot leave it standing for somebody else's edit. The default is prevented
    // because the focus is already where Tab was asking it to go: the field is one element, moved
    // over another column, so the caret never leaves it and there is no blur to commit twice.
    stepMonth (event, step) {
      const count = this.bars ? this.bars.length : 0
      const index = this.editing
      if (index === null || count === 0) return
      this.sendMonth(index, this.editingAmount, event.target.value)
      this.stepping = true
      this.$nextTick(() => { this.stepping = false })
      this.editing = (index + step + count) % count
      this.$nextTick(() => {
        const field = this.$refs.monthField
        const el = Array.isArray(field) ? field[0] : field
        if (el) el.select()
      })
    }
  },
  watch: {
    // A field belongs to the row it was opened on. Every commit closes it already; this is for the
    // ways a row changes that are not one — a shape pressed, or the figure above retyped — which
    // would otherwise leave a caret standing over somebody else's month.
    bars () {
      if (this.stepping) return
      this.editing = null
    }
  }
}
</script>

<style scoped>
  /* The card's own chrome, on its own root. The page it sits in owns the LAYOUT — how wide it is
     and how it wraps — because that is a statement about the row, not about the card. */
  .clone-metric {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 12px 16px 10px;
  }

  /* What may be done to the year, over the right-hand end of it: the row is what the buttons act on
     and they stand at its end rather than over its middle, where they would be the first thing read
     about a picture they are only the controls for. */
  .clone-actions {
    display: flex;
    justify-content: flex-end;
    gap: 2px;
    margin: 10px 0 0;
    align-items: center;
  }
  /* Quiet until it is reached for. These are things that may be done to the year and not readings of
     it, and the year is what the card is about. */
  .clone-action {
    color: rgba(0, 0, 0, 0.45) !important;
  }
  .clone-action:hover {
    color: rgba(0, 0, 0, 0.8) !important;
  }

  .clone-metric__label {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
  }
  /* Ties each box to the colour its section is drawn in below: the spend stack, the revenue bars
     and the decay plots. */
  .clone-metric__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 7px;
    flex: none;
  }
  /* The months and the year beside them, filling whatever the card's other contents leave between
     the value and the footer — which is a lot, the box stretching to the height of the card beside
     it and its chart. */
  .clone-metric__bars {
    position: relative;
    flex: 1 1 auto;
    min-height: 48px;
    margin-top: 14px;
    display: flex;
    align-items: flex-end;
  }
  /* THE COLUMN THAT IS NOT A MONTH, set apart by a rule down its left. It is the same three
     components drawn the same way, at its OWN scale — a year is twelve months tall — and the rule is
     what says the two axes are not one. Its width and the air before it are the seasonality total's
     own, so the rule under this chart and the rule under that one stand on one line and both plots
     end at the same place. */
  .clone-metric__bar--apart {
    padding-left: 12px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
  }
  /* And the word under it, in the room the column above it takes. It is not one of twelve of
     anything, so it keeps its name rather than an initial, and reads in the small grey the other
     readings wear rather than as a thirteenth tick. */
  .clone-metric__bar-tick--apart {
    padding-left: 12px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
  }
  /* Written to be either a span or a button without changing what it looks like: the browser's own
     chrome is stripped back so a column that can be typed into is the same picture as one that
     cannot, and the only difference is the cursor and what a hover does. */
  .clone-metric__bar {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    outline: none;
  }
  .clone-metric__bar--live {
    cursor: pointer;
  }
  /* The two regions of a column, above the line and below it. They are laid out by the column's own
     flex, so a month's figure is still a flex item of the same box as its segments and a bar of no
     height still has its number standing over the line rather than under it. */
  .clone-metric__bar-up {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex-grow: 0;
    flex-shrink: 0;
    min-height: 0;
  }
  /* Read downwards from the line, which is why it starts where the other one ends. */
  .clone-metric__bar-down {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex-grow: 0;
    flex-shrink: 0;
    min-height: 0;
  }
  /* The line itself. Darker than the row's ground, because which side of it a segment falls on is
     the first thing to read once there is a side to fall on, and deaf to the pointer so the column
     under it keeps its click. */
  .clone-metric__bar-zero {
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.25);
    pointer-events: none;
  }
  /* The column under the pointer, and the one being typed into, lit the same way: the segments keep
     their own colours — the split is what the stack is FOR — and the column is picked out from
     behind them instead. */
  .clone-metric__bar--live:hover,
  .clone-metric__bar--editing {
    background-color: rgba(0, 0, 0, 0.06);
  }
  .clone-metric__bar--live:focus-visible {
    box-shadow: 0 0 0 2px rgba(67, 160, 71, 0.4);
  }
  /* THE MONTH, TYPED. Over the plot rather than in the column — twelve columns to a box leaves no
     room for a figure in dollars — on its own white ground so the bars behind it do not read through
     the number standing over them. */
  .clone-metric__month {
    position: absolute;
    z-index: 1;
    padding: 2px 6px;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.3;
    text-align: center;
    color: #2E7D32;
    background-color: #ffffff;
    border: 1px solid #43A047;
    border-radius: 3px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
    outline: none;
  }
  /* Sized by flex-basis and not by height: the figure over the stack is a flex item of the same
     column, so the two are laid out by one box and a bar of no height still has its number standing
     over the axis rather than under it. Neither of them shrinks — what the row states is what is
     drawn, and the room the figures stand in is made by the row before it gets here. */
  .clone-metric__bar-fill {
    display: block;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
  }
  /* The same green the revenue chart below is drawn in, and the same figure the box on the left
     shows on its own. */
  .clone-metric__bar-fill--base {
    background-color: #43A047;
  }
  /* What the curves return, in the colour that return carries in the card beside it. */
  .clone-metric__bar-fill--media {
    background-color: #7E57C2;
  }
  /* What the season is credited with, in the colour it is drawn in wherever it appears in this
     wizard — its own bars, the waterfall's middle bar, and this segment. The same colour on both
     sides of the line: a month that gives revenue up to the season and one that takes it from the
     season are the same component seen from either side of its own zero. */
  .clone-metric__bar-fill--season {
    background-color: #FFB300;
  }
  /* And the spot events, in the SAME colour, which is what makes the two of them one band. They were
     blue — two hues for the two signed components, on the argument that a reader has to tell them
     apart at three pixels tall. Nothing else agrees: the waterfall credits them to one bar, the pool
     they claim from is one pot, and the section that asks for them is one section. Against the base
     and the media they are one component — revenue that is neither what the model sells on its own
     nor anything the media bought — and where the two DO have to be told apart, which is the editor
     they are drawn in, the fill says so. The same colour on both sides of the line, too: a month
     that gives revenue up to the season and one that takes it from the season are the same component
     seen from either side of its own zero. */
  .clone-metric__bar-fill--spot {
    background-color: #FFB300;
  }
  /* THE MONTH'S OWN FIGURE, OVER ITS OWN COLUMN. Small and grey: it is twelve numbers at once and
     what is being compared across them is the shape, which is drawn — the figures are what the shape
     is worth, read one at a time. They never wrap and never widen a column: a compact form is five
     or six characters and the ellipsis is the honest answer for the card too narrow to hold one. */
  .clone-metric__bar-value {
    flex: 0 0 auto;
    padding-bottom: 2px;
    font-size: 0.55rem;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .clone-metric__bar--live:hover .clone-metric__bar-value {
    color: #2E7D32;
  }
  /* The month being typed into keeps its room and loses its figure: the field is standing exactly
     where it was, and two of them would be one figure the user typed and one the typing has moved
     past. */
  .clone-metric__bar-value--hidden {
    visibility: hidden;
  }
  /* The letters under the columns. Same gap and same flex as the bars themselves, so each one is
     centred on the column above it however many months the window carries. */
  .clone-metric__bar-axis {
    display: flex;
    margin-top: 3px;
  }
  .clone-metric__bar-tick {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;
    font-size: 9px;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }
  /* A year and a half of months onwards, a letter is wider than the bar it names. */
  .clone-metric__bar-axis--dense .clone-metric__bar-tick {
    font-size: 7px;
  }
  /* What the figure IS, where the card asks a second question under it. In the small grey the
     footers use rather than in the card's own uppercase label: that label names the section, and
     this names one of the things inside it. */
  .clone-metric__ask {
    display: block;
    margin-top: 2px;
    font-size: 0.7rem;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
  }
  /* The same name over a figure that is read rather than typed: nothing to put a caret in, so
     nothing to promise it. */
  .clone-metric__ask--static {
    cursor: default;
  }
  /* The headline row: the field, and whatever is read off it. It takes the width it needs and stops
     — the readings sit next to the figure, not at the far end of the card, where a wide box would
     otherwise strand them. They ride the bottom of the row so that a reading and the figure it is a
     reading of share a line, the field's own name standing above both. */
  .clone-metric__headline {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 28px;
  }
  .clone-metric__headline-field {
    flex: 0 0 auto;
    min-width: 0;
  }
  /* The row is aligned on the two NAMES rather than on the two figures, and the figures then line up
     by construction: each side is a name over a value in the same type, so anchoring the first lines
     to each other anchors the second ones too — without this side having to guess at the height of
     the input's furniture opposite it. */
  /* The line under the headline field. The hint's own weight and colour, which is what this wizard
     uses everywhere for the one thing a section says about itself that is not a figure. */
  .clone-metric__hint {
    margin: 4px 0 0;
    font-size: 0.7rem;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.5);
  }
  .clone-metric__aside {
    flex: 0 0 auto;
    min-width: 0;
  }
  /* An aside sizes to its own content, so its line is kept to a column rather than let widen it. */
  .clone-metric__aside .clone-metric__hint {
    max-width: 200px;
  }
  .clone-metric__aside-value {
    display: block;
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.55);
    white-space: nowrap;
  }
  /* The editable headline. Sized and weighted like the static value it replaced, with the input
     furniture stripped back so the box still reads as a figure rather than as a form. Its width is
     the longest figure it takes, in the digits of its own type — see `maxLength`. */
  .clone-metric__input {
    margin: 0;
    padding: 0;
  }
  /* As wide as the longest figure it takes and no wider. `ch` is the width of a digit in the field's
     OWN type, which is why the size is stated here as well as on the input inside: the unit is read
     against the box it is written on. A character of slack over `maxLength`, the figures being set
     in a medium weight where `ch` is measured on a regular one. */
  .clone-metric__input--short {
    width: 14ch;
    font-size: 1.6rem;
  }
  .clone-metric__input >>> input {
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 1.45;
    padding: 0;
    max-height: none;
  }
  .clone-metric__input >>> .v-text-field__prefix,
  .clone-metric__input >>> .v-text-field__suffix {
    font-size: 1.6rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__input >>> .v-input__slot::before {
    border-color: rgba(0, 0, 0, 0.12);
  }
  /* The unit, beside the section's own name and lighter than it: it names what the figures are
     counted in rather than being one of them. */
  .clone-metric__unit {
    margin-left: 6px;
    text-transform: none;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.38);
  }
  /* How far this figure has been moved off the source's own. */
  .clone-metric__factor {
    margin-left: auto;
    font-weight: 500;
    color: #4B67B8;
    letter-spacing: 0;
    text-transform: none;
  }
  .clone-metric__foot {
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 0.75rem;
  }
  .clone-metric__foot-label {
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-metric__foot-value {
    font-weight: 500;
  }
  /* WHAT THE MONTH IS MADE OF, in a card rather than a line: the month, its components each beside
     the colour it is drawn in on the chart, and the figure they come to under a rule. Read against
     the column it was opened from, so the order is the stack's own and the dots are the only thing
     that has to be matched by eye. */
  .clone-bar-tip {
    position: absolute;
    bottom: calc(100% + 8px);
    z-index: 2;
    padding: 7px 11px 8px;
    border-radius: 4px;
    color: #ffffff;
    background-color: rgba(33, 33, 33, 0.94);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.26);
    pointer-events: none;
  }
  .clone-bar-tip__head {
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 3px;
  }
  .clone-bar-tip__row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    line-height: 1.6;
  }
  /* The sum, under a rule: it is not a fourth component, it is what the three above it come to. */
  .clone-bar-tip__row--sum {
    margin-top: 3px;
    padding-top: 3px;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    font-weight: 600;
  }
  .clone-bar-tip__dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  /* The sum has no colour of its own — it is not drawn anywhere — and still takes the dot's room, so
     its name starts on the same line as the three above it. */
  .clone-bar-tip__dot--none {
    background-color: transparent;
  }
  .clone-bar-tip__name {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
  }
  /* The figures in one column and of one width, so three amounts read as a column of amounts rather
     than as three sentences of different lengths. */
  .clone-bar-tip__value {
    flex: none;
    font-variant-numeric: tabular-nums;
  }
  /* A label and its figure travel together, so a footer can carry two of them and still read as
     two facts rather than four. */
  .clone-foot-pair {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
</style>
